import EventPopup from "@/components/Event/EventPopup.vue";
import { mergeMilestoneClaims } from "@/event/domain/mergeEventMilestones.ts";
import { applyGrandPrizeStatePatch } from "@/event/domain/mergeGrandPrizeState";
import {
	buildMilestoneMapFromEventConfig,
	deriveMilestoneStatuses,
} from "@/event/domain/milestones.ts";
import { isPastEndDate } from "@/event/session/eventEndDate.ts";
import { onEventEnd } from "@/event/session/eventEndSchedule.ts";
import {
	applyOverviewPatch,
	catchUpEventEnd as catchUpEventEndStore,
	type OverviewPatchDeps,
} from "@/event/session/overviewPatch.ts";
import { ResourceType } from "@/game/entities/config/resources.ts";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter.ts";
import router from "@/router";
import {
	getSettlementsStore,
	isSettlementsStoreRegistered,
} from "@/store/settlementsStoreRegistry.ts";
import {
	eventPopupIds,
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore.ts";
import useResourcesStore from "@/store/useResourcesStore.ts";
import useShopStore from "@/store/useShopStore.ts";
import type {
	ClaimablesCoordinates,
	EventGrandPrizeConfig,
	EventGrandPrizeState,
	EventLeaderboardResult,
	EventMapCollectablesResult,
	EventMilestoneConfig,
	EventMilestoneProgressValue,
	EventMilestonesState,
	EventOverviewResult,
	EventStarterBuildingGrant,
} from "@/types";
import type { MilestoneItem } from "@/types/event.ts";
import { MilestoneStatus } from "@/types/event.ts";
import type { ShopItem } from "@/types/shop.ts";
import type { EventCategory } from "@/types/ui.ts";
import { SkinTypes } from "@/types/ui.ts";
import { roundNumber } from "@/utils/number.ts";
import {
	createImmutableState,
	getImmutableStateProperty,
	setImmutableStateProperty,
} from "@/utils/state.ts";
import { translate } from "@/utils/translate.ts";
import { defineStore, storeToRefs } from "pinia";
import { computed, markRaw, type ComputedRef } from "vue";
export type EventAttributes = {
	type: EventCategory | null;
	level_unlock?: number;
	has_super_clicker_offer?: boolean;
	event_pass_store_id?: string;
	milestones?: EventMilestoneConfig[];
	grand_prizes?: EventGrandPrizeConfig;
	start_building_claims?: Array<{
		frameName: string;
		amount: number;
	}>;
};

export type EventStoreState = EventOverviewResult;

type EventStore = {
	id: ComputedRef<string | null>;
	attributes: ComputedRef<EventAttributes>;
	milestones: ComputedRef<Map<number, MilestoneItem> | null>;
	hasUnclaimedMilestones: ComputedRef<boolean>;
	leaderboards: ComputedRef<EventLeaderboardResult | null>;
	leaderboardLoadTimestamp: ComputedRef<number | null>;
	isActive: ComputedRef<boolean>;
	serverEventActive: ComputedRef<boolean>;
	isPopupActive: ComputedRef<boolean>;
	/** Root/App overlay intro; not the replay layer inside `EventPopup.vue`. */
	isIntroPopupActive: ComputedRef<boolean>;
	startDate: ComputedRef<Date | null>;
	endDate: ComputedRef<Date | null>;
	baseEventiumIncome: ComputedRef<number>;
	eventGeneration: ComputedRef<number>;
	mapClaimablesCoordinates: ComputedRef<ClaimablesCoordinates[]>;
	collectedByType: ComputedRef<Record<number, number>>;
	eventPassPurchased: ComputedRef<boolean>;
	initBuildingsClaimed: ComputedRef<boolean>;
	introDismissed: ComputedRef<boolean>;
	starterMapCleaned: ComputedRef<boolean>;
	starterBuildingGrants: ComputedRef<EventStarterBuildingGrant[]>;
	grandPrizeState: ComputedRef<EventGrandPrizeState | null>;
	$setState: (value: Partial<EventStoreState>) => void;
	setClaimedMilestoneIds: (
		milestoneIds: Record<number, EventMilestoneProgressValue>,
	) => void;
	/** Deep clone of current claimed snapshot (e.g. debugging). */
	getClaimedMilestoneIdsSnapshot: () => Record<
		number,
		EventMilestoneProgressValue
	> | null;
	restoreClaimedMilestoneIds: (
		snapshot: Record<number, EventMilestoneProgressValue> | null,
	) => void;
	/** Updates claimed snapshot after rewards are applied locally, before persistence. */
	mergeClaimedMilestonesOptimistic: (partial: EventMilestonesState) => void;
	setGrandPrizeState: (state: EventGrandPrizeState | null) => void;
	/** Updates grand-prize snapshot after local chest opens, before persistence. */
	mergeGrandPrizeState: (partial: EventGrandPrizeState) => void;
	getGrandPrizeStateSnapshot: () => EventGrandPrizeState | null;
	restoreGrandPrizeState: (snapshot: EventGrandPrizeState | null) => void;
	setEventPopupActive: (active: boolean) => void;
	setEventIntroPopupActive: (active: boolean) => void;
	setInitBuildingsClaimed: (claimed: boolean) => void;
	setIntroDismissed: (dismissed: boolean) => void;
	setStarterMapCleaned: (cleaned: boolean) => void;
	setLeaderboardData(data: EventLeaderboardResult | null): void;
	setTileClaimablesCoordinates(coordinates: ClaimablesCoordinates[]): void;
	incrementCollectedByType(type: number, amount: number): void;
	resetCollectedByType(): void;
	getEventMapCollectablesPayload(): EventMapCollectablesResult;
	/** Snapshot for `save.eventMilestones` — persisted on tick saves; omit when no active event / empty. */
	getClaimedMilestonesPayloadForSave(): EventMilestonesState | undefined;
	/** Snapshot for `save.eventGrandPrizeState` — full or partial; BE merges provided fields. */
	getGrandPrizeStatePayloadForSave(): EventGrandPrizeState | undefined;
	setEventGenerationMax(value: number): void;
	catchUpEventEnd(): void;
};

export type EventState = {
	id: string | null;
	attributes: EventAttributes;
	claimedMilestoneIds: Record<number, EventMilestoneProgressValue> | null;
	milestones: Map<number, MilestoneItem> | null;
	leaderboardLoadTimestamp: number | null;
	leaderboards: EventLeaderboardResult | null;
	isServerActive: boolean;
	isPopupActive: boolean;
	isIntroPopupActive: boolean;
	startDate: Date | null;
	endDate: Date | null;
	baseEventiumIncome: number;
	mapClaimablesCoordinates: ClaimablesCoordinates[];
	collectedByType: Record<number, number>;
	eventPassPurchased: boolean;
	eventGeneration: number;
	initBuildingsClaimed: boolean;
	introDismissed: boolean;
	starterMapCleaned: boolean;
	starterBuildingGrants: EventStarterBuildingGrant[];
	grandPrizeState: EventGrandPrizeState | null;
};

const initialState: EventState = {
	id: null,
	attributes: {
		type: null,
	},
	claimedMilestoneIds: null,
	milestones: null,
	leaderboardLoadTimestamp: null,
	leaderboards: null,
	isServerActive: false,
	isPopupActive: false,
	isIntroPopupActive: false,
	startDate: null,
	endDate: null,
	baseEventiumIncome: 0,
	mapClaimablesCoordinates: [],
	collectedByType: {},
	eventPassPurchased: false,
	eventGeneration: 0,
	initBuildingsClaimed: false,
	introDismissed: false,
	starterMapCleaned: false,
	starterBuildingGrants: [],
	grandPrizeState: null,
};

const STORE_NAME = "event";

export default defineStore(STORE_NAME, (): EventStore => {
	const state = createImmutableState<EventState>(initialState);
	const endedCleanupOnceKeys = new Set<string>();

	const id = getImmutableStateProperty(state, "id", null);

	const attributes = getImmutableStateProperty(state, "attributes", {
		...initialState.attributes,
	});

	const leaderboards = getImmutableStateProperty(state, "leaderboards", null);

	const leaderboardLoadTimestamp = getImmutableStateProperty(
		state,
		"leaderboardLoadTimestamp",
		null,
	);

	const setClaimedMilestoneIds = (
		milestoneIds: Record<number, EventMilestoneProgressValue>,
	): void => {
		setImmutableStateProperty(state, "claimedMilestoneIds", milestoneIds);
	};

	const mergeClaimedMilestonesOptimistic = (
		partial: EventMilestonesState,
	): void => {
		const merged = mergeMilestoneClaims(
			state.value.claimedMilestoneIds,
			partial,
		);
		setImmutableStateProperty(
			state,
			"claimedMilestoneIds",
			merged ?? partial,
		);
	};

	const getClaimedMilestoneIdsSnapshot = (): Record<
		number,
		EventMilestoneProgressValue
	> | null => {
		const raw = state.value.claimedMilestoneIds;
		if (raw === null) return null;
		return JSON.parse(JSON.stringify(raw)) as Record<
			number,
			EventMilestoneProgressValue
		>;
	};

	const restoreClaimedMilestoneIds = (
		snapshot: Record<number, EventMilestoneProgressValue> | null,
	): void => {
		setImmutableStateProperty(state, "claimedMilestoneIds", snapshot);
	};

	const setGrandPrizeState = (next: EventGrandPrizeState | null): void => {
		setImmutableStateProperty(state, "grandPrizeState", next);
	};

	const mergeGrandPrizeState = (partial: EventGrandPrizeState): void => {
		setImmutableStateProperty(
			state,
			"grandPrizeState",
			applyGrandPrizeStatePatch(state.value.grandPrizeState, partial),
		);
	};

	const getGrandPrizeStateSnapshot = (): EventGrandPrizeState | null => {
		const raw = state.value.grandPrizeState;
		if (raw === null) return null;
		return JSON.parse(JSON.stringify(raw)) as EventGrandPrizeState;
	};

	const restoreGrandPrizeState = (
		snapshot: EventGrandPrizeState | null,
	): void => {
		setImmutableStateProperty(state, "grandPrizeState", snapshot);
	};

	const milestones = computed((): Map<number, MilestoneItem> | null => {
		const resourcesStore = useResourcesStore();
		const { coinsIncome, eventiumAccumulated } =
			storeToRefs(resourcesStore);
		const claimed = getImmutableStateProperty(
			state,
			"claimedMilestoneIds",
			null,
		);

		const configMap = attributes.value.milestones?.length
			? buildMilestoneMapFromEventConfig(
					attributes.value.milestones,
					coinsIncome.value ?? 0,
				)
			: null;

		if (configMap === null) return null;

		return deriveMilestoneStatuses(
			configMap,
			claimed.value,
			eventiumAccumulated.value,
			eventPassPurchased.value,
		);
	});

	const hasUnclaimedMilestones = computed((): boolean => {
		if (!milestones.value) return false;

		for (const milestone of milestones.value.values()) {
			// Kontrola Free vetvy
			if (milestone.status === MilestoneStatus.CLAIMABLE) {
				return true;
			}
			// Kontrola Premium vetvy (iba ak existuje a je v stave claimable)
			if (
				milestone.premium &&
				milestone.premium.status === MilestoneStatus.CLAIMABLE
			) {
				return true;
			}
		}
		return false;
	});

	const eventPassPurchasedFromOverview = getImmutableStateProperty(
		state,
		"eventPassPurchased",
		false,
	);

	const eventPassPurchased = computed((): boolean => {
		if (eventPassPurchasedFromOverview.value) return true;

		const storeId = attributes.value.event_pass_store_id;
		if (!storeId) return false;

		const shopStore = useShopStore();
		return !!shopStore.items?.find(
			(offer: ShopItem): boolean =>
				offer.store_id === storeId && offer.num_of_purchases > 0,
		);
	});

	const serverEventActive = getImmutableStateProperty(
		state,
		"isServerActive",
		false,
	);

	const isActive = computed((): boolean => {
		if (isPastEndDate(state.value.endDate)) {
			return false;
		}

		const attributes = getImmutableStateProperty(
			state,
			"attributes",
			initialState.attributes,
		);
		if (!isSettlementsStoreRegistered()) {
			return serverEventActive.value && !!attributes.value.level_unlock;
		}
		const settlementStore = getSettlementsStore();
		const { mainLevel } = storeToRefs(settlementStore);

		return (
			serverEventActive.value &&
			!!attributes.value.level_unlock &&
			mainLevel.value >= attributes.value.level_unlock
		);
	});

	const isPopupActive = getImmutableStateProperty(
		state,
		"isPopupActive",
		false,
	);

	const isIntroPopupActive = getImmutableStateProperty(
		state,
		"isIntroPopupActive",
		false,
	);

	const eventGeneration = getImmutableStateProperty(
		state,
		"eventGeneration",
		0,
	);

	const initBuildingsClaimed = getImmutableStateProperty(
		state,
		"initBuildingsClaimed",
		false,
	);

	const introDismissed = getImmutableStateProperty(
		state,
		"introDismissed",
		false,
	);

	const starterMapCleaned = getImmutableStateProperty(
		state,
		"starterMapCleaned",
		false,
	);

	const starterBuildingGrants = getImmutableStateProperty(
		state,
		"starterBuildingGrants",
		[],
	);

	const grandPrizeState = getImmutableStateProperty(
		state,
		"grandPrizeState",
		null,
	);

	const startDate = getImmutableStateProperty(state, "startDate", null);
	const endDate = getImmutableStateProperty(state, "endDate", null);

	const setCollectables = (
		collectables: EventMapCollectablesResult,
	): void => {
		setImmutableStateProperty(
			state,
			"collectedByType",
			collectables.collected_by_type,
		);
		setTileClaimablesCoordinates(collectables.collectables);
	};

	const $setState = (value?: Partial<EventStoreState>): void => {
		applyOverviewPatch(patchDeps(), value);
	};

	const setLeaderboardData = (data: EventLeaderboardResult | null): void => {
		setImmutableStateProperty(
			state,
			"leaderboardLoadTimestamp",
			Date.now(),
		);
		setImmutableStateProperty(state, "leaderboards", data);
	};

	const setEventPopupActive = (active: boolean): void => {
		if (active && isPastEndDate(state.value.endDate)) {
			setImmutableStateProperty(state, "isPopupActive", false);
			usePopupStore().hidePopup();
			return;
		}

		setImmutableStateProperty(state, "isPopupActive", active);

		const emitter = useEmitter();
		const popupStore = usePopupStore();

		if (active) {
			emitter.emit(EmitterEvents.PhaserTileDeselect);
			router.push("/");

			const eventType = state.value.attributes?.type;

			popupStore.showPopup({
				id: PopupDictionary.EVENT,
				type: PopupType.EVENT,
				heading: translate(
					`event.${eventType}_event.title.non_translatable`,
				),
				width: 26,
				maxHeight: 47,
				isPaddingless: true,
				skin: SkinTypes.DARK,
				component: markRaw(EventPopup),
				spriteData: {
					slug: `${eventType}-event`,
					key: 1,
					size: 416,
					hasOverlap: true,
				},
				scaleDelay: 400,
				forceDividerLine: true,
				closeCallback: (): void => {
					setImmutableStateProperty(state, "isPopupActive", false);
				},
			});
		} else {
			// Explicitly close the popup to synchronize the UI with the store state
			// eslint-disable-next-line no-console
			console.log(
				"setEventPopupActive >> false >> popupStore.hidePopup()",
			);

			// Trigger hidePopup only if the currently displayed popup ID is one of the event popups
			if (
				eventPopupIds.includes(popupStore.state?.id as PopupDictionary)
			) {
				popupStore.hidePopup();
			}
			setImmutableStateProperty(state, "isPopupActive", false);
		}
	};

	const setEventIntroPopupActive = (active: boolean): void => {
		setImmutableStateProperty(state, "isIntroPopupActive", active);
	};

	const setInitBuildingsClaimed = (claimed: boolean): void => {
		setImmutableStateProperty(state, "initBuildingsClaimed", claimed);
	};

	const setIntroDismissed = (dismissed: boolean): void => {
		setImmutableStateProperty(state, "introDismissed", dismissed);
	};

	const setStarterMapCleaned = (cleaned: boolean): void => {
		setImmutableStateProperty(state, "starterMapCleaned", cleaned);
	};

	const baseEventiumIncome = computed((): number => {
		const resourcesStore = useResourcesStore();
		const resources = storeToRefs(resourcesStore);

		if (!resources[ResourceType.EVENTIUM_INCOME].value) return 0;

		// UI should show base income (without temporary income boost).
		return roundNumber(resources[ResourceType.EVENTIUM_INCOME].value);
	});

	const mapClaimablesCoordinates = getImmutableStateProperty(
		state,
		"mapClaimablesCoordinates",
		[],
	);

	const setTileClaimablesCoordinates = (
		coordinates: ClaimablesCoordinates[],
	): void => {
		setImmutableStateProperty(
			state,
			"mapClaimablesCoordinates",
			coordinates,
		);
	};

	const collectedByType = getImmutableStateProperty(
		state,
		"collectedByType",
		{},
	);

	const incrementCollectedByType = (type: number, amount: number): void => {
		const current = state.value.collectedByType[type] || 0;
		setImmutableStateProperty(state, "collectedByType", {
			...state.value.collectedByType,
			[type]: current + amount,
		});
	};

	const resetCollectedByType = (): void => {
		setImmutableStateProperty(state, "collectedByType", {});
	};

	const getEventMapCollectablesPayload = (): EventMapCollectablesResult => {
		return {
			collectables: state.value.mapClaimablesCoordinates,
			collected_by_type: state.value.collectedByType,
		};
	};

	const getClaimedMilestonesPayloadForSave = ():
		| EventMilestonesState
		| undefined => {
		if (state.value.id === null) return undefined;
		const cid = state.value.claimedMilestoneIds;
		if (cid === null || Object.keys(cid).length === 0) return undefined;
		return cid as unknown as EventMilestonesState;
	};

	const getGrandPrizeStatePayloadForSave = ():
		| EventGrandPrizeState
		| undefined => {
		if (state.value.id === null) return undefined;
		return getGrandPrizeStateSnapshot() ?? undefined;
	};

	const setEventGenerationMax = (value: number): void => {
		const next = Math.max(state.value.eventGeneration, value);
		setImmutableStateProperty(state, "eventGeneration", next);
	};

	const patchDeps = (): OverviewPatchDeps => ({
		state,
		initialAttributes: initialState.attributes,
		endedCleanupOnceKeys,
		setCollectables,
		setTileClaimablesCoordinates,
		closeEventPopup: (): void => setEventPopupActive(false),
	});

	const catchUpEventEnd = (): void => {
		catchUpEventEndStore(patchDeps());
	};

	onEventEnd(catchUpEventEnd);

	return {
		id,
		attributes,
		milestones,
		hasUnclaimedMilestones,
		isActive,
		serverEventActive,
		isPopupActive,
		isIntroPopupActive,
		startDate,
		endDate,
		baseEventiumIncome,
		leaderboards,
		leaderboardLoadTimestamp,
		mapClaimablesCoordinates,
		collectedByType,
		eventPassPurchased,
		initBuildingsClaimed,
		introDismissed,
		starterMapCleaned,
		starterBuildingGrants,
		grandPrizeState,
		$setState,
		setClaimedMilestoneIds,
		getClaimedMilestoneIdsSnapshot,
		restoreClaimedMilestoneIds,
		mergeClaimedMilestonesOptimistic,
		setGrandPrizeState,
		mergeGrandPrizeState,
		getGrandPrizeStateSnapshot,
		restoreGrandPrizeState,
		setEventPopupActive,
		setEventIntroPopupActive,
		setInitBuildingsClaimed,
		setIntroDismissed,
		setStarterMapCleaned,
		setLeaderboardData,
		eventGeneration,
		setTileClaimablesCoordinates,
		incrementCollectedByType,
		resetCollectedByType,
		getEventMapCollectablesPayload,
		getClaimedMilestonesPayloadForSave,
		getGrandPrizeStatePayloadForSave,
		setEventGenerationMax,
		catchUpEventEnd,
	};
});
